import { globalItemDataStore } from '../OverworldAnimation';
import type { Item } from './managers/ItemManager';
import ItemManager from './managers/ItemManager';

const STORAGE_KEY = 'item_data';

// TODO(pkmng-143): Once the API exposes these items, remove this seed.
// Temporary client-side entries for items that do not yet have an API row.
const LOCAL_ONLY_ITEMS: Record<string, Item> = {
    town_map: {
        id: 'town_map',
        name: 'Town Map',
        description: 'A map of the Boriken region. Use it to check location details.',
        price: 0,
    } as Item,
};

export default class ItemDataLoader {
    store: Record<string, Item>;
    alwaysFetchFromAPI: boolean;

    constructor() {
        this.store = {};
        this.alwaysFetchFromAPI = true;
    }

    getItem(id: string): Item | undefined {
        return this.store[id];
    }

    applyLocalOnlyItems(): void {
        Object.keys(LOCAL_ONLY_ITEMS).forEach((id) => {
            this.store[id] = LOCAL_ONLY_ITEMS[id];
            (globalItemDataStore as Record<string, Item>)[id] = LOCAL_ONLY_ITEMS[id];
        });
    }

    async loadItemData(): Promise<void> {
        try {
            if (!this.alwaysFetchFromAPI) {
                const cached = this.loadFromLocalStorage();
                if (Object.keys(cached).length > 0) {
                    this.store = cached;
                    Object.keys(cached).forEach((id) => {
                        (globalItemDataStore as Record<string, Item>)[id] = cached[id];
                    });
                    this.applyLocalOnlyItems();
                    // Quick progress animation for localStorage load
                    const { animateQuickProgress } = await import('../OverworldAnimation.js');
                    await animateQuickProgress(
                        'ITEM_DATA',
                        `Loaded ${Object.keys(cached).length} items from cache`
                    );
                    return;
                }
            }

            const { updateLoadingProgress } = await import('../OverworldAnimation.js');
            updateLoadingProgress('ITEM_DATA', 'Loading item data from API...');
            const manager = new ItemManager();
            await manager.loadItemsFromAPI();
            Object.keys(manager.store).forEach((id) => {
                const item = manager.getItem(id);
                if (item) {
                    (globalItemDataStore as Record<string, Item>)[id] = item;
                }
            });
            this.saveToLocalStorage(manager.store);
            this.store = manager.store;
            this.applyLocalOnlyItems();
        } catch (error) {
            const message = error instanceof Error ? error.message : String(error);
            console.error(`Error loading item data: ${message}`);
        }
    }

    saveToLocalStorage(db: Record<string, Item>): void {
        try {
            localStorage.setItem(STORAGE_KEY, JSON.stringify(db));
            console.log('Item database saved to localStorage');
        } catch (error) {
            const message = error instanceof Error ? error.message : String(error);
            console.error(`Failed to save item database to localStorage: ${message}`);
        }
    }

    loadFromLocalStorage(): Record<string, Item> {
        try {
            const raw = localStorage.getItem(STORAGE_KEY);
            if (!raw || raw.trim() === '') return {};
            return JSON.parse(raw) as Record<string, Item>;
        } catch (error) {
            const message = error instanceof Error ? error.message : String(error);
            console.error(`Failed to load item database from localStorage: ${message}`);
            return {};
        }
    }
}

export const itemDataLoader = new ItemDataLoader();
